import { useState, useMemo, useEffect } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Shield, ShieldCheck, Plus, Pencil, Pause, Play, Trash2, BarChart3, Loader2,
  AlertTriangle, AlertCircle, LinkIcon, Copy, Check, XCircle, MessageSquare, ExternalLink, EyeOff, Sparkles,
  ArrowLeft, ChevronRight, Lock,
} from 'lucide-react'
import { useAuth } from '../context/AuthProvider'
import { notify } from '../components/Toast'
import {
  listMyPersonas, pausePersona, resumePersona, deletePersona, getPersonaActivity,
  generateInviteToken, listInviteTokens, revokeInviteToken, deleteInviteToken,
  getChatSessions, hideChatSession, summarizeSession,
  type InviteToken, type InviteTokenStatus, type PersonaData,
} from '../api/client'
import PlanSelection from '../components/PlanSelection'
import TierBadge from '../components/TierBadge'
import ConfirmDialog from '../components/ConfirmDialog'
import Pagination from '../components/Pagination'
import { timeAgo } from '../lib/relativeTime'

function SectionHeader({ icon: Icon, title, action }: { icon: React.ElementType; title: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
        <Icon size={16} className="text-gray-500 dark:text-gray-400" /> {title}
      </h3>
      {action}
    </div>
  )
}

function StatusPill({ persona }: { persona: PersonaData }) {
  const cls = persona.isPaused ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400'
    : persona.isActive ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400'
    : 'text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400'
  const label = persona.isPaused ? 'Paused' : persona.isActive ? 'Active' : 'Draft'
  return <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{label}</span>
}

export default function Dashboard() {
  const { entitlements, entitlementLimit, hasEntitlement, refreshMe } = useAuth()
  const maxPersonas = entitlementLimit('idfy.persona.maxCount', 1)
  const canShare = hasEntitlement('idfy.persona.share', true)
  const canAnalytics = hasEntitlement('idfy.persona.analytics', true)
  const allowMultiple = hasEntitlement('idfy.persona.multiplePersonas', false) || maxPersonas > 1
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get('persona')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [newLabel, setNewLabel] = useState('')
  const [generating, setGenerating] = useState(false)
  // Sticky inline error for the Generate form — surfaces plan-limit and similar
  // constraints that a transient toast would hide too fast (the message points
  // the user at a fix, so it should persist until they act on it or dismiss).
  const [generateError, setGenerateError] = useState<string | null>(null)
  const [summaries, setSummaries] = useState<Record<string, string>>({})
  const [summarizing, setSummarizing] = useState<string | null>(null)
  const [confirmDeletePersona, setConfirmDeletePersona] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Pagination state — separate knobs per section. Filter defaults to "active" because
  // revoked links are usually historical noise; users opening the panel want the
  // currently-usable ones first.
  const [tokensStatus, setTokensStatus] = useState<InviteTokenStatus>('active')
  const [tokensPage, setTokensPage] = useState(0)
  const [tokensSize, setTokensSize] = useState(10)
  const [sessionsPage, setSessionsPage] = useState(0)
  const [sessionsSize, setSessionsSize] = useState(10)

  const { data: personas = [], isLoading } = useQuery({
    queryKey: ['my-personas'],
    queryFn: listMyPersonas,
    retry: false,
  })

  const selectedPersona = useMemo(
    () => (selectedId ? personas.find((p) => p.id === selectedId) : null) || null,
    [personas, selectedId],
  )
  const personaId = selectedPersona?.id

  const { data: activity } = useQuery({ queryKey: ['persona-activity', personaId], queryFn: () => personaId ? getPersonaActivity(personaId) : null, enabled: Boolean(personaId) })

  // Query keys include pagination params so each page/filter combo is independently cached.
  // placeholderData: keepPreviousData keeps the previous page rendered while the next one
  // loads — no layout flash between pages.
  const { data: tokensData, isPlaceholderData: tokensPlaceholder } = useQuery({
    queryKey: ['invite-tokens', personaId, tokensStatus, tokensPage, tokensSize],
    queryFn: () => personaId ? listInviteTokens(personaId, { status: tokensStatus, page: tokensPage, size: tokensSize }) : null,
    enabled: Boolean(personaId),
    placeholderData: keepPreviousData,
  })
  const { data: sessionsData, isPlaceholderData: sessionsPlaceholder } = useQuery({
    queryKey: ['chat-sessions', personaId, sessionsPage, sessionsSize],
    queryFn: () => personaId ? getChatSessions(personaId, { page: sessionsPage, size: sessionsSize }) : null,
    enabled: Boolean(personaId),
    placeholderData: keepPreviousData,
  })

  // When the selected persona changes or the filter tab flips, reset the page cursor so
  // we don't land on a page that no longer exists in the new set.
  useEffect(() => { setTokensPage(0) }, [personaId, tokensStatus])
  useEffect(() => { setSessionsPage(0) }, [personaId])
  // Clear the inline Generate error when switching personas — the error belonged
  // to the previous persona's plan/context and would be misleading here.
  useEffect(() => { setGenerateError(null) }, [personaId])

  const reloadList = () => queryClient.invalidateQueries({ queryKey: ['my-personas'] })
  const reloadPersonaScoped = () => {
    queryClient.invalidateQueries({ queryKey: ['invite-tokens', personaId] })
    queryClient.invalidateQueries({ queryKey: ['chat-sessions', personaId] })
    queryClient.invalidateQueries({ queryKey: ['persona-activity', personaId] })
  }

  const openPersona = (id: string) => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('persona', id); return n })
  const backToList = () => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('persona'); return n })

  const handlePauseResume = async () => {
    if (!personaId) return
    try {
      if (selectedPersona?.isPaused) { await resumePersona(personaId); notify.success('Your persona is now active.') }
      else { await pausePersona(personaId); notify.success('Your persona has been paused.') }
      reloadList()
    } catch (err) { notify.error((err as { message?: string })?.message || 'Something went wrong. Please try again.') }
  }

  const handleDelete = () => setConfirmDeletePersona(true)
  const confirmDelete = async () => {
    if (!personaId) return
    setDeleting(true)
    try {
      await deletePersona(personaId)
      notify.success('Your persona has been deleted.')
      setConfirmDeletePersona(false)
      backToList()
      reloadList()
    } catch (err) {
      notify.error((err as { message?: string })?.message || 'Something went wrong. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const handleGenerateToken = async () => {
    if (!personaId) return
    setGenerating(true)
    setGenerateError(null)
    try {
      await generateInviteToken(personaId, newLabel || undefined)
      setNewLabel('')
      reloadPersonaScoped()
      notify.success('Invite link has been created.')
    } catch (err) {
      const message = (err as { message?: string })?.message || 'Something went wrong. Please try again.'
      // Plan-gated errors ("Active invite link limit reached for your plan …")
      // render inline below the form with an Upgrade CTA instead of as a toast —
      // the remediation path is in the message, so the user needs it visible
      // while they act on it, not disappearing after 6s.
      if (/limit reached|does not allow/i.test(message)) {
        setGenerateError(message)
      } else {
        notify.error(message)
      }
    }
    finally { setGenerating(false) }
  }

  const handleRevokeToken = async (tokenId: string) => {
    if (!personaId) return
    try { await revokeInviteToken(personaId, tokenId); reloadPersonaScoped(); notify.success('Invite link has been revoked.') }
    catch (err) { notify.error((err as { message?: string })?.message || 'Something went wrong. Please try again.') }
  }

  const handleDeleteToken = async (tokenId: string) => {
    if (!personaId) return
    try { await deleteInviteToken(personaId, tokenId); reloadPersonaScoped(); notify.success('Invite link has been deleted.') }
    catch (err) { notify.error((err as { message?: string })?.message || 'Something went wrong. Please try again.') }
  }

  const handleHideSession = async (sessionId: string) => {
    if (!personaId) return
    try { await hideChatSession(personaId, sessionId); reloadPersonaScoped(); notify.success('Conversation dismissed.') }
    catch (err) { notify.error((err as { message?: string })?.message || 'Something went wrong. Please try again.') }
  }

  const handleSummarize = async (sessionId: string) => {
    if (!personaId || summarizing) return
    setSummarizing(sessionId)
    try {
      const res = await summarizeSession(personaId, sessionId)
      setSummaries((prev) => ({ ...prev, [sessionId]: res.summary }))
    } catch (err) { notify.error((err as { message?: string })?.message || 'Something went wrong. Please try again.') }
    finally { setSummarizing(null) }
  }

  const copyInviteLink = (token: InviteToken) => {
    navigator.clipboard?.writeText(`${window.location.origin}/p/${selectedPersona?.handle}?t=${token.token}`)
    setCopiedId(token.id)
    setTimeout(() => setCopiedId(null), 2000)
    notify.success('Link copied to clipboard.')
  }

  if (isLoading) return <div className="flex justify-center py-24"><Loader2 size={32} className="animate-spin text-brand-600" /></div>

  if (!entitlements?.categoryCode || !entitlements?.tierCode) {
    return <PlanSelection onSelected={refreshMe} />
  }

  // ── Empty state: no personas at all ────────────────────────────────
  if (personas.length === 0) {
    const canCreate = maxPersonas > 0
    const features = [
      { icon: ShieldCheck, title: 'Consent-first', desc: 'Your persona speaks only after you grant explicit consent.' },
      { icon: LinkIcon, title: 'Secure sharing', desc: 'Revocable invite links with per-link usage tracking.' },
      { icon: BarChart3, title: 'Full visibility', desc: 'Every interaction logged — allow, refuse, and block insights.' },
    ]
    return (
      <div className="relative max-w-3xl mx-auto py-12 px-4">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute left-1/2 -top-20 -translate-x-1/2 h-80 w-[40rem] bg-brand-500/10 blur-3xl rounded-full" />
        </div>

        <div className="relative card p-8 md:p-12">
          {entitlements?.categoryCode && entitlements?.tierCode && (
            <div className="flex flex-wrap items-center justify-center gap-2 text-[11px] font-medium uppercase tracking-wider">
              <span className="px-2 py-0.5 rounded bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-400">{entitlements.categoryCode}</span>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">{entitlements.tierCode}</span>
              <span className="text-gray-500 normal-case tracking-normal">up to {maxPersonas} persona{maxPersonas === 1 ? '' : 's'}</span>
            </div>
          )}

          <div className="mt-6 mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 ring-4 ring-brand-500/10 flex items-center justify-center shadow-lg">
            <Shield size={32} className="text-white" strokeWidth={2.2} />
          </div>

          <h1 className="mt-6 text-center text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-50">Create Your Persona</h1>
          <p className="mt-3 text-center text-gray-600 dark:text-gray-400 max-w-xl mx-auto leading-relaxed">
            Build an AI persona that represents you or your business — with your consent and within your defined boundaries.
          </p>

          <div className="mt-8 flex justify-center">
            {canCreate ? (
              <Link to="/persona/setup?new=1" className="btn-primary text-base px-6 py-3"><Plus size={18} /> Get Started</Link>
            ) : maxPersonas <= 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md">Your current plan does not include persona creation. Contact your administrator to upgrade.</p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md">You do not have permission to create a persona. Contact your administrator.</p>
            )}
          </div>

          <div className="mt-10 pt-8 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex flex-col items-start gap-2">
                <div className="w-9 h-9 rounded-lg bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center">
                  <Icon size={18} className="text-brand-600 dark:text-brand-400" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 text-xs text-center text-gray-500 dark:text-gray-500">
            Fully reversible — pause or delete your persona at any time.
          </p>
        </div>
      </div>
    )
  }

  // ── Grid of personas ───────────────────────────────────────────────
  if (!selectedPersona) {
    const canCreateMore = personas.length < maxPersonas && (allowMultiple || personas.length === 0)
    const atLimit = personas.length >= maxPersonas

    return (
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Your Personas</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {personas.length} of {maxPersonas} persona{maxPersonas === 1 ? '' : 's'} used
            </p>
          </div>
          {entitlements?.categoryCode && entitlements?.tierCode && (
            <TierBadge category={entitlements.categoryCode} tier={entitlements.tierCode} size="md" />
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {personas.map((p) => (
            <button key={p.id} type="button" onClick={() => openPersona(p.id!)}
              className={`group relative text-left card p-5 hover:border-brand-500/60 dark:hover:border-brand-500/60 hover:shadow-lg transition-all ${p.isPaused ? 'opacity-75' : ''}`}>
              <div className="flex items-start gap-3">
                {p.ownerLogoUrl ? (
                  <img src={p.ownerLogoUrl} alt="" className="w-11 h-11 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-brand-500/70 to-brand-700 flex items-center justify-center flex-shrink-0">
                    <Shield size={20} className="text-white" strokeWidth={2.2} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{p.name}</h3>
                    <StatusPill persona={p} />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    /{p.handle} &middot; {p.type}
                  </p>
                </div>
                <ChevronRight size={16} className="text-gray-300 dark:text-gray-600 group-hover:text-brand-500 transition-colors flex-shrink-0 mt-1" />
              </div>
              {p.description && (
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
                  {p.description}
                </p>
              )}
            </button>
          ))}

          {canCreateMore ? (
            <Link to="/persona/setup?new=1"
              className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 hover:border-brand-500 dark:hover:border-brand-500 hover:bg-brand-50/30 dark:hover:bg-brand-900/10 text-gray-500 hover:text-brand-700 dark:text-gray-400 dark:hover:text-brand-300 transition-colors min-h-[9.5rem] p-5">
              <Plus size={18} />
              <span className="text-sm font-medium">Create persona</span>
            </Link>
          ) : atLimit && (
            <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-gray-400 dark:text-gray-500 min-h-[9.5rem] p-5 text-center">
              <Lock size={16} />
              <p className="text-xs font-medium">Plan limit reached</p>
              <Link to="/plan" className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline">Upgrade for more</Link>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Detail view for selected persona ───────────────────────────────
  const tokensPage_content = tokensData?.content ?? []
  const activeCount  = tokensData?.activeCount  ?? 0
  const revokedCount = tokensData?.revokedCount ?? 0
  const sessionsPage_content = sessionsData?.content ?? []

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-8">
      {/* Consolidated persona header — name, handle, tier, status, description,
          and the Edit/Pause/Delete actions all live here. The previous layout
          had a separate "Persona" card below that repeated name + handle +
          type; on a page that's already titled by this persona, the restated
          card was redundant surface and visual noise. */}
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <button type="button" onClick={backToList}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400 transition-colors mb-2">
              <ArrowLeft size={13} /> All personas
            </button>
            <div className="flex items-center gap-3">
              {selectedPersona.ownerLogoUrl && (
                <img src={selectedPersona.ownerLogoUrl} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
              )}
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">{selectedPersona.name}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">/{selectedPersona.handle} &middot; {selectedPersona.type}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {entitlements?.categoryCode && entitlements?.tierCode && (
              <TierBadge category={entitlements.categoryCode} tier={entitlements.tierCode} size="md" />
            )}
            <StatusPill persona={selectedPersona} />
          </div>
        </div>

        {selectedPersona.description && (
          <p className="text-sm text-gray-600 dark:text-gray-300 max-w-2xl">{selectedPersona.description}</p>
        )}

        {/* Paused-state banner — more prominent than the status pill alone.
            Shows a full-width amber callout with Resume promoted to primary action
            so users who wonder why their persona isn't responding have an obvious
            recovery path without hunting through the action row. */}
        {selectedPersona.isPaused && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <Pause size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">This persona is paused</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                Your persona is not publicly accessible while paused. Existing invite links remain valid but visitors will be shown a paused message.
              </p>
            </div>
            <button type="button" onClick={handlePauseResume}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold
                         bg-amber-100 text-amber-800 hover:bg-amber-200
                         dark:bg-amber-800/40 dark:text-amber-200 dark:hover:bg-amber-800/60
                         focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors">
              <Play size={12} /> Resume
            </button>
          </div>
        )}

        {!selectedPersona.isActive && !selectedPersona.consentedAt && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Consent required</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">Your persona is not publicly accessible until you give explicit consent.</p>
              <Link to={`/persona/setup?id=${personaId}`} className="text-xs font-medium text-brand-600 hover:text-brand-700 mt-2 inline-block">Complete setup &rarr;</Link>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Link to={`/persona/setup?id=${personaId}`} className="btn-primary text-sm"><Pencil size={14} /> Edit</Link>
          <button type="button" onClick={handlePauseResume} className="btn-secondary text-sm">
            {selectedPersona.isPaused ? <><Play size={14} /> Resume</> : <><Pause size={14} /> Pause</>}
          </button>
          {/* Delete is text-only + red — visible but demoted. ConfirmDialog still gates the action. */}
          <button
            type="button"
            onClick={handleDelete}
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium px-2 py-1 rounded-md
                       text-red-600 hover:text-red-700 hover:bg-red-50
                       dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-500/10
                       focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors">
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      {selectedPersona.isActive && canShare && (
        <section className="card p-6 space-y-4">
          {/* Count moved down into the filter tabs — they're actionable there
              (clicking filters) whereas a header count was decorative. */}
          <SectionHeader icon={LinkIcon} title="Invite Links" />

          <div className="flex gap-2">
            <input type="text" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Label (optional — e.g. Team, Client, Public)" className="input flex-1 text-sm" maxLength={50} />
            <button type="button" onClick={handleGenerateToken} disabled={generating} className="btn-primary text-sm whitespace-nowrap">
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Generate
            </button>
          </div>

          {/* Plan-limit / sharing-disabled error, rendered inline under the form.
              Styled to match the "Consent required" amber warning — red because
              this is a hard constraint, not a setup prompt. Stays visible until
              the user acts or explicitly dismisses. */}
          {generateError && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <AlertCircle size={16} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">{generateError}</p>
                <p className="text-xs text-red-700/90 dark:text-red-400/90 mt-1">
                  Revoke an active link below to free up a slot, or upgrade your plan.
                </p>
                <Link to="/plan" className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-200">
                  Upgrade plan →
                </Link>
              </div>
              <button type="button" onClick={() => setGenerateError(null)}
                className="flex-shrink-0 p-1 -m-1 rounded text-red-400 hover:text-red-600 dark:hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                aria-label="Dismiss">
                <XCircle size={14} />
              </button>
            </div>
          )}

          {/* Filter tabs — segmented control. Counts come from the paginated response so
              they stay accurate even across pages. "Revoked" tab is hidden when empty to
              keep the UI tidy for the common case where nothing has been revoked yet. */}
          {(activeCount + revokedCount) > 0 && (
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-gray-800/50 text-xs">
              {([
                { key: 'active',  label: 'Active',  count: activeCount },
                ...(revokedCount > 0 ? [{ key: 'revoked' as const, label: 'Revoked', count: revokedCount }] : []),
                { key: 'all',     label: 'All',     count: activeCount + revokedCount },
              ] as { key: InviteTokenStatus; label: string; count: number }[]).map((t) => (
                <button key={t.key} type="button" onClick={() => setTokensStatus(t.key)}
                  className={`px-3 py-1 rounded-md font-medium transition-colors ${
                    tokensStatus === t.key
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                  aria-pressed={tokensStatus === t.key}>
                  {t.label} <span className="text-gray-400 ml-1">{t.count}</span>
                </button>
              ))}
            </div>
          )}

          {tokensPage_content.length > 0 ? (
            <div className={`divide-y divide-gray-100 dark:divide-gray-700 transition-opacity ${tokensPlaceholder ? 'opacity-60' : ''}`}>
              {tokensPage_content.map((t) => (
                <div key={t.id} className={`flex items-center gap-3 py-3 ${!t.active ? 'opacity-60' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium truncate ${t.active ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 line-through'}`}>{t.label || 'Invite link'}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        t.active
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                      }`}>{t.active ? 'active' : 'revoked'}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                      <span className="font-mono">...{t.token.slice(-8)}</span>
                      <span>{t.usageCount} uses</span>
                      {t.lastUsedAt && <span title={new Date(t.lastUsedAt).toLocaleString()}>last used {timeAgo(t.lastUsedAt)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {t.active && (
                      <>
                        <button type="button" onClick={() => copyInviteLink(t)} title="Copy invite link" aria-label="Copy invite link"
                          className="p-2 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors">
                          {copiedId === t.id ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />}
                        </button>
                        <button type="button" onClick={() => handleRevokeToken(t.id)} title="Revoke access" aria-label="Revoke invite link"
                          className="p-2 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors">
                          <XCircle size={15} />
                        </button>
                      </>
                    )}
                    <button type="button" onClick={() => handleDeleteToken(t.id)} title="Permanently delete" aria-label="Permanently delete invite link"
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">
              {tokensStatus === 'active'  ? 'No active invite links. Generate one above to share your persona.'
               : tokensStatus === 'revoked' ? 'No revoked invite links.'
               : 'No invite links yet. Generate one to share your persona.'}
            </p>
          )}

          {tokensData && (
            <Pagination
              page={tokensData.page}
              pageSize={tokensData.size}
              totalElements={tokensData.totalElements}
              totalPages={tokensData.totalPages}
              loading={tokensPlaceholder}
              onPageChange={setTokensPage}
              onPageSizeChange={(s) => { setTokensSize(s); setTokensPage(0) }}
            />
          )}
        </section>
      )}

      {canAnalytics && sessionsData && sessionsData.totalElements > 0 && (
        <section className="card p-6 space-y-4">
          <SectionHeader icon={MessageSquare} title="Recent Conversations"
            action={<span className="text-xs text-gray-400 font-medium">{sessionsData.totalElements} session{sessionsData.totalElements === 1 ? '' : 's'}</span>} />

          <div className={`divide-y divide-gray-100 dark:divide-gray-700 transition-opacity ${sessionsPlaceholder ? 'opacity-60' : ''}`}>
            {sessionsPage_content.map((s) => {
              // Prefer the session's own invite token; falls back to the first active token on
              // the current invite-links page (won't always be present, but good enough — the
              // "Open conversation" button simply hides when we can't build a URL).
              const tokenValue = s.inviteToken || tokensPage_content.find((t) => t.active)?.token
              const chatUrl = tokenValue ? `/p/${selectedPersona.handle}?t=${tokenValue}&s=${s.sessionId}` : null
              return (
                <div key={s.sessionId} className="py-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{s.lastMessage}</p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                        <span>{s.messageCount} messages</span>
                        <span title={new Date(s.lastActivity).toLocaleString()}>{timeAgo(s.lastActivity)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button type="button" onClick={() => handleSummarize(s.sessionId)}
                        disabled={summarizing === s.sessionId}
                        title="AI Summary" aria-label="Generate AI summary"
                        className="p-2 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors disabled:opacity-50">
                        {summarizing === s.sessionId ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                      </button>
                      {chatUrl && (
                        <a href={chatUrl} target="_blank" rel="noreferrer" title="Open conversation" aria-label="Open conversation in new tab"
                          className="p-2 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors">
                          <ExternalLink size={15} />
                        </a>
                      )}
                      <button type="button" onClick={() => handleHideSession(s.sessionId)} title="Dismiss" aria-label="Dismiss conversation"
                        className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                        <EyeOff size={15} />
                      </button>
                    </div>
                  </div>
                  {summaries[s.sessionId] && (
                    <div className="ml-1 pl-3 border-l-2 border-amber-300 dark:border-amber-700">
                      <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{summaries[s.sessionId]}</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <Pagination
            page={sessionsData.page}
            pageSize={sessionsData.size}
            totalElements={sessionsData.totalElements}
            totalPages={sessionsData.totalPages}
            loading={sessionsPlaceholder}
            onPageChange={setSessionsPage}
            onPageSizeChange={(s) => { setSessionsSize(s); setSessionsPage(0) }}
          />
        </section>
      )}

      <ConfirmDialog
        open={confirmDeletePersona}
        tone="danger"
        title="Delete this persona?"
        description="All invite links and chat history will be removed. This cannot be undone."
        confirmLabel={deleting ? 'Deleting…' : 'Delete persona'}
        cancelLabel="Cancel"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => { if (!deleting) setConfirmDeletePersona(false) }}
      />

      {/* Activity is always rendered for members with the analytics entitlement,
          even before the query resolves or if it temporarily errors — zeros
          are a perfectly valid state and hiding the card when `activity`
          is undefined made the section flicker in/out during refetch and
          left users wondering where their stats went after sharing a link. */}
      {canAnalytics && (
        <section className="card p-6 space-y-4">
          <SectionHeader icon={BarChart3} title="Activity"
            action={activity?.lastActivity
              ? <span className="text-xs text-gray-400" title={new Date(activity.lastActivity).toLocaleString()}>Last activity {timeAgo(activity.lastActivity)}</span>
              : <span className="text-xs text-gray-400">No activity yet</span>} />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'Page Visits',  value: activity?.totalVisits       ?? 0 },
              { label: 'Interactions', value: activity?.totalInteractions ?? 0 },
              { label: 'Allowed',      value: activity?.allowedResponses  ?? 0 },
              { label: 'Refused',      value: activity?.refusedResponses  ?? 0 },
              { label: 'Blocked',      value: activity?.blockedResponses  ?? 0 },
            ].map((s) => (
              <div key={s.label} className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4 text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{s.value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
